import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  ListItemButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth, useWorkspaces } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { WorkspaceTypes } from '@flashbacktech/flashbackclient/api';

const WorkspacesTab: React.FC = () => {
  const { authState } = useAuth();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [workspaces, setWorkspaces] = React.useState<WorkspaceTypes.Workspace[]>([]);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [wsName, setWsName] = React.useState('');
  const [inviteEmails, setInviteEmails] = React.useState('');
  const [defaultInviteRole, setDefaultInviteRole] = React.useState<WorkspaceTypes.AccessType>(WorkspaceTypes.AccessType.READ);
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [createInfo, setCreateInfo] = React.useState<string | null>(null);

  // Workspace details (update, manage users)
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<WorkspaceTypes.Workspace | null>(null);
  const [wsNameEdit, setWsNameEdit] = React.useState('');
  const [addingEmail, setAddingEmail] = React.useState('');
  const [addingRole, setAddingRole] = React.useState<WorkspaceTypes.AccessType>(WorkspaceTypes.AccessType.READ);
  const [savingWs, setSavingWs] = React.useState(false);

  // Match Profile modal input style
  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      height: '40px',
    },
  } as const;
  
  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Always fetch from API to get full Workspace type with all properties (orgId, etc.)
      // Context workspaces are simplified {id, name} and don't have all required properties
      const res = await withTokenRefresh(() => apiClient.getWorkspaces());
      if (res.success) {
        setWorkspaces(res.workspaces || []);
      } else {
        setError('Failed to load workspaces');
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, [withTokenRefresh, apiClient]);

  const canDeleteWorkspace = React.useCallback(
    (w: WorkspaceTypes.Workspace): boolean => {
      const meEmail = authState?.user?.email?.toLowerCase();
      if (!meEmail) return false;
      // GetWorkspacesResponse includes users array
      const workspaceWithUsers = w as any;
      const users = workspaceWithUsers.users || [];
      return users.some((u: WorkspaceTypes.WorkspaceUser) => u.user?.email?.toLowerCase() === meEmail && u.role === WorkspaceTypes.AccessType.ADMIN);
    },
    [authState?.user?.email]
  );

  const handleDeleteWorkspace = async (id: string) => {
    if (!window.confirm('Delete this workspace? This action cannot be undone.')) return;
    setLoading(true);
    setError(null);
    try {
      const res = await withTokenRefresh(() => apiClient.deleteWorkspace(id));
      if (!res.success) throw new Error('Failed to delete workspace');
      await load();
      
      // Notify AuthContext to reload workspaces
      try {
        window.dispatchEvent(new CustomEvent('forceReloadWorkspaces'));
      } catch (e) {
        console.warn('[WorkspacesTab] Failed to dispatch forceReloadWorkspaces event:', e);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to delete workspace');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, [load]);

  const openDetails = async (w: WorkspaceTypes.Workspace) => {
    setSelected(w);
    setWsNameEdit(w.name);
    setAddingEmail('');
    setAddingRole(WorkspaceTypes.AccessType.READ);
    setDetailsError(null);
    setDetailsLoading(true);
    setDetailsOpen(true);
    try {
      const res = await withTokenRefresh(() => apiClient.getWorkspace(w.id));
      if (res.success && res.workspace) {
        setSelected(res.workspace as any);
        setWsNameEdit(res.workspace.name);
      } else if (!res.success) {
        setDetailsError('Failed to load workspace');
      }
    } catch (e: any) {
      setDetailsError(e?.message || 'Failed to load workspace');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleUpdateName = async () => {
    if (!selected) return;
    setSavingWs(true);
    setDetailsError(null);
    try {
      const res = await withTokenRefresh(() =>
        apiClient.updateWorkspace(selected.id, { name: wsNameEdit.trim() })
      );
      if (!res.success) throw new Error('Failed to update workspace');
      // refresh details
      const g = await withTokenRefresh(() => apiClient.getWorkspace(selected.id));
      if (g.success && g.workspace) {
        setSelected(g.workspace as any);
        setWsNameEdit(g.workspace.name);
      }
      await load();
    } catch (e: any) {
      setDetailsError(e?.message || 'Failed to update workspace');
    } finally {
      setSavingWs(false);
    }
  };

  const handleAddUser = async () => {
    if (!selected) return;
    const email = addingEmail.trim().toLowerCase();
    if (!email) return;
    // validate format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setDetailsError('Invalid email format. Users must belong to the same organization.');
      return;
    }
    setSavingWs(true);
    setDetailsError(null);
    try {
      const orgUsersRes = await withTokenRefresh(() => apiClient.getOrganizationUsers());
      const byEmail = new Map<string, string>();
      (orgUsersRes.data || []).forEach((u) => {
        if (u.email) byEmail.set(u.email.toLowerCase(), u.id);
      });
      const userId = byEmail.get(email);
      if (!userId)
        throw new Error(
          'This email is not part of your organization. Users must belong to the same organization.'
        );
      const res = await withTokenRefresh(() =>
        apiClient.addUserToWorkspace(selected.id, { userId, role: addingRole })
      );
      if (!res.success) throw new Error('Failed to add user');
      // refresh details
      const g = await withTokenRefresh(() => apiClient.getWorkspace(selected.id));
      if (g.success && g.workspace) setSelected(g.workspace as any);
      setAddingEmail('');
      setAddingRole(WorkspaceTypes.AccessType.READ);
      await load();
    } catch (e: any) {
      setDetailsError(e?.message || 'Failed to add user');
    } finally {
      setSavingWs(false);
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (!selected) return;
    if (!window.confirm('Remove this user from the workspace?')) return;
    setSavingWs(true);
    setDetailsError(null);
    try {
      const res = await withTokenRefresh(() =>
        apiClient.removeUserFromWorkspace(selected.id, userId)
      );
      if (!res.success) throw new Error('Failed to remove user');
      const g = await withTokenRefresh(() => apiClient.getWorkspace(selected.id));
      if (g.success && g.workspace) setSelected(g.workspace as any);
      await load();
    } catch (e: any) {
      setDetailsError(e?.message || 'Failed to remove user');
    } finally {
      setSavingWs(false);
    }
  };

  const handleCreate = async () => {
    if (!wsName.trim()) {
      setCreateError('Workspace name is required');
      return;
    }
    setCreating(true);
    setCreateError(null);
    setCreateInfo(null);
    try {
      const createRes = await withTokenRefresh(() =>
        apiClient.createWorkspace({ name: wsName.trim() })
      );
      if (!createRes.success || !createRes.workspace?.id) {
        throw new Error('Failed to create workspace');
      }
      const wsId = createRes.workspace.id;

      const emails = inviteEmails
        .split(',')
        .map((e) => e.trim().toLowerCase())
        .filter((e) => e.length > 0);

      if (emails.length > 0) {
        // Basic email format validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalidFormat: string[] = [];
        const validFormatEmails: string[] = [];
        for (const e of emails) {
          if (!emailRegex.test(e)) invalidFormat.push(e);
          else validFormatEmails.push(e);
        }

        if (invalidFormat.length) {
          setCreateError(
            `These emails are invalid: ${invalidFormat.join(
              ', '
            )}. Users must belong to the same organization.`
          );
        }

        // Fetch org users and build email->userId map
        const orgUsersRes = await withTokenRefresh(() =>
          apiClient.getOrganizationUsers()
        );
        const byEmail = new Map<string, string>();
        (orgUsersRes.data || []).forEach((u) => {
          if (u.email) byEmail.set(u.email.toLowerCase(), u.id);
        });

        const notInOrg: string[] = [];
        for (const email of validFormatEmails) {
          const userId = byEmail.get(email);
          if (!userId) {
            notInOrg.push(email);
            continue;
          }
          try {
            await withTokenRefresh(() =>
              apiClient.addUserToWorkspace(wsId, { userId, role: defaultInviteRole })
            );
          } catch (e) {
            // collect but continue
            notInOrg.push(email);
          }
        }
        if (notInOrg.length && invalidFormat.length) {
          setCreateError(
            `Some emails are invalid and some are not part of your organization. Invalid: ${invalidFormat.join(
              ', '
            )}. Not in your organization: ${notInOrg.join(
              ', '
            )}. Users must belong to the same organization.`
          );
        } else if (notInOrg.length) {
          setCreateError(
            `The following emails are not part of your organization. Users must belong to the same organization: ${notInOrg.join(
              ', '
            )}`
          );
        } else if (invalidFormat.length) {
          setCreateError(
            `These emails are invalid: ${invalidFormat.join(
              ', '
            )}. Users must belong to the same organization.`
          );
        } else {
          setCreateInfo('Workspace created and users added successfully');
        }
      } else {
        setCreateInfo('Workspace created successfully');
      }

      // Refresh list and close
      await load();
      
      // Notify AuthContext to reload workspaces (force reload)
      try {
        window.dispatchEvent(new CustomEvent('forceReloadWorkspaces'));
      } catch (e) {
        console.warn('[WorkspacesTab] Failed to dispatch forceReloadWorkspaces event:', e);
      }
      
      setWsName('');
      setInviteEmails('');
      // keep dialog open to show messages; user can close
    } catch (e: any) {
      setCreateError(e?.message || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: { xs: '14px', sm: '16px' } }}>
          Workspaces
        </Typography>
        <Button
          variant="contained"
          onClick={() => setCreateOpen(true)}
          sx={{
            bgcolor: '#617BFF',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: '13px', sm: '14px' },
            borderRadius: '8px',
            height: '36px',
            width: { xs: '100%', sm: 'auto' },
            '&:hover': { bgcolor: '#4A67E3' },
          }}
        >
          Create workspace
        </Button>
      </Box>

      <List
        dense
        sx={{ flex: 1, overflowY: 'auto', border: '1px solid #E9EAEB', borderRadius: '12px' }}
      >
        {workspaces.map((w) => (
          <ListItem
            key={w.id}
            divider
            disablePadding
            secondaryAction={
              canDeleteWorkspace(w) ? (
                <IconButton
                  edge="end"
                  aria-label="delete workspace"
                  onClick={() => handleDeleteWorkspace(w.id)}
                  title="Delete workspace"
                >
                  <DeleteIcon />
                </IconButton>
              ) : undefined
            }
          >
            <ListItemButton onClick={() => openDetails(w)}>
              <ListItemText
                primary={w.name}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#414651',
                  },
                }}
                secondary={`ID: ${w.id}${(w as any).users?.length ? ` • ${(w as any).users.length} users` : ''}`}
                secondaryTypographyProps={{
                  sx: {
                    fontWeight: 400,
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: '#717680',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
        {workspaces.length === 0 && !loading && (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ color: '#6B7280' }}>No workspaces found.</Typography>
          </Box>
        )}
      </List>

      {/* Bottom spacer under the workspaces list */}
      <Box sx={{ height: 24 }} />

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { border: '1px solid #E9EAEB', borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: '16px', color: '#101828' }}>
          Create workspace
        </DialogTitle>
        <DialogContent>
          {createError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {createError}
            </Alert>
          )}
          {createInfo && (
            <Alert severity="success" sx={{ mt: 1 }}>
              {createInfo}
            </Alert>
          )}
          <TextField
            label="Workspace name"
            value={wsName}
            onChange={(e) => setWsName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 2, ...inputStyle }}
          />
          <TextField
            label="Invite users by email (comma-separated)"
            value={inviteEmails}
            onChange={(e) => setInviteEmails(e.target.value)}
            placeholder="user1@example.com, user2@example.com"
            fullWidth
            size="small"
            sx={{ mt: 2, ...inputStyle }}
          />
          <FormControl fullWidth sx={{ mt: 2 }} size="small">
            <InputLabel id="invite-role-label">Default role</InputLabel>
            <Select
              labelId="invite-role-label"
              label="Default role"
              value={defaultInviteRole}
              onChange={(e) => setDefaultInviteRole(e.target.value as WorkspaceTypes.AccessType)}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' },
              }}
            >
              <MenuItem value={WorkspaceTypes.AccessType.READ}>READ</MenuItem>
              <MenuItem value={WorkspaceTypes.AccessType.WRITE}>WRITE</MenuItem>
              <MenuItem value={WorkspaceTypes.AccessType.ADMIN}>ADMIN</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={creating}
            sx={{
              bgcolor: 'transparent',
              color: '#414651',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              mr: 1,
              borderRadius: '8px',
              height: '36px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating}
            sx={{
              bgcolor: '#617BFF',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: '8px',
              height: '36px',
              '&:hover': { bgcolor: '#4A67E3' },
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Workspace Details */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { border: '1px solid #E9EAEB', borderRadius: '12px' } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: '16px',
            color: '#101828',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          Workspace details
          {selected && canDeleteWorkspace(selected) && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => selected && handleDeleteWorkspace(selected.id)}
              sx={{ textTransform: 'none', fontWeight: 600, height: '32px', borderRadius: '8px' }}
            >
              Delete
            </Button>
          )}
        </DialogTitle>
        <DialogContent>
          {detailsError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {detailsError}
            </Alert>
          )}
          {detailsLoading && <LinearProgress sx={{ mt: 1 }} />}
          {selected && (
            <>
              <TextField
                label="Workspace name"
                value={wsNameEdit}
                onChange={(e) => setWsNameEdit(e.target.value)}
                fullWidth
                size="small"
                sx={{ mt: 2, ...inputStyle }}
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 1,
                  mt: 2,
                }}
              >
                <TextField
                  label="Add user by email"
                  value={addingEmail}
                  onChange={(e) => setAddingEmail(e.target.value)}
                  placeholder="user@example.com"
                  size="small"
                  sx={{ flex: 1, ...inputStyle }}
                />
                <FormControl sx={{ minWidth: { xs: '100%', sm: 140 } }} size="small">
                  <InputLabel id="add-role-label">Role</InputLabel>
                  <Select
                    labelId="add-role-label"
                    label="Role"
                    value={addingRole}
                    onChange={(e) => setAddingRole(e.target.value as WorkspaceTypes.AccessType)}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '40px' },
                    }}
                  >
                    <MenuItem value={WorkspaceTypes.AccessType.READ}>READ</MenuItem>
                    <MenuItem value={WorkspaceTypes.AccessType.WRITE}>WRITE</MenuItem>
                    <MenuItem value={WorkspaceTypes.AccessType.ADMIN}>ADMIN</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={handleAddUser}
                  disabled={savingWs}
                  sx={{
                    bgcolor: '#617BFF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    borderRadius: '8px',
                    height: '36px',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': { bgcolor: '#4A67E3' },
                  }}
                >
                  Add
                </Button>
              </Box>

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#414651',
                  mt: 3,
                  mb: 1,
                }}
              >
                Members
              </Typography>
              <List dense sx={{ border: '1px solid #E9EAEB', borderRadius: '12px' }}>
                {((selected as any).users || []).map((u: WorkspaceTypes.WorkspaceUser) => (
                  <ListItem
                    key={u.userId}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleRemoveUser(u.userId)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        `${u.user?.name || ''} ${u.user?.lastName || ''}`.trim() ||
                        u.user?.email ||
                        u.userId
                      }
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: 600,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: '#414651',
                        },
                      }}
                      secondary={
                        <>
                          <Chip size="small" label={u.role} sx={{ mr: 1 }} />
                          {u.user?.email}
                        </>
                      }
                      secondaryTypographyProps={{
                        sx: {
                          fontWeight: 400,
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: '#717680',
                        },
                      }}
                    />
                  </ListItem>
                ))}
                {(!(selected as any).users || (selected as any).users.length === 0) && (
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ color: '#717680', fontSize: '14px', lineHeight: '20px' }}>
                      No members
                    </Typography>
                  </Box>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {(() => {
            // Vérifier si le nom a changé
            const hasNameChanged = selected && wsNameEdit.trim() !== selected.name.trim();
            const isDisabled = savingWs || !hasNameChanged;

            return (
              <>
                <Button
                  onClick={() => setDetailsOpen(false)}
                  disabled={savingWs}
                  sx={{
                    bgcolor: 'transparent',
                    color: '#414651',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    mr: 1,
                    borderRadius: '8px',
                    height: '36px',
                  }}
                >
                  Close
                </Button>
                <Button
                  onClick={handleUpdateName}
                  variant="contained"
                  disabled={isDisabled}
                  sx={{
                    bgcolor: hasNameChanged && !savingWs ? '#617BFF' : '#E5E7EB',
                    color: hasNameChanged && !savingWs ? 'white' : '#9CA3AF',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    borderRadius: '8px',
                    height: '36px',
                    '&:hover': {
                      bgcolor: hasNameChanged && !savingWs ? '#4A67E3' : '#E5E7EB',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#E5E7EB',
                      color: '#9CA3AF',
                    },
                  }}
                >
                  {savingWs ? 'Saving...' : 'Save name'}
                </Button>
              </>
            );
          })()}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkspacesTab;
